/* @jsxRuntime automatic */
/* @jsxImportSource @superweb/css */

import { createPortal } from "react-dom";
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
  type SetStateAction,
} from "react";
import { useId, useOverlay } from "react-aria";

import { cssFns, type Style } from "@superweb/css";
import { useLocale } from "@superweb/intl";

import { Button } from "../buttons/button";
import { DialogContext } from "../dialog/dialog";
import { icons } from "../icons";
import { useIsMobile } from "../mobile-context";
import { PopoverContextProvider } from "../popover";
import { useCustomScrollbar } from "../scrollbar/scrollbar";
import { useSnackbarContext } from "../snackbar/snackbar-container";
import { useUiColors } from "../theme";
import { useTypo } from "../typo";
import { useUiOptions } from "../ui-options-context";

export const SlideoverContext = createContext<
  | {
      container: HTMLDivElement | null;
      onStackChange: (value: SetStateAction<(() => void)[]>) => void;
      preventCloseElements?: Set<HTMLElement>;
      addPreventCloseElement?: (el: HTMLElement) => void;
      removePreventCloseElement?: (el: HTMLElement | null) => void;
      isNested?: boolean;
    }
  | undefined
>(undefined);
export const SlideoverContextProvider = SlideoverContext.Provider;

const isElementFocusable = (node: unknown): node is HTMLElement => {
  return (
    !!node &&
    node instanceof HTMLElement &&
    document.body.contains(node) &&
    typeof node.focus === "function"
  );
};

type FocusableElement = Element & { focus: () => void };

const focusableElementsSelector =
  [
    "input:not([disabled]):not([type=hidden])",
    "select:not([disabled])",
    "textarea:not([disabled])",
    "button:not([disabled])",
    "a[href]",
    "area[href]",
    "summary",
    "iframe",
    "object",
    "embed",
    "audio[controls]",
    "video[controls]",
    "[contenteditable]",
  ].join(":not([hidden]),") + ",[tabindex]:not([disabled]):not([hidden])";

const getNearestNodes = (
  origin: Node | null,
): {
  origin: FocusableElement | null;
  nextNode: FocusableElement | null;
  previousNode: FocusableElement | null;
} => {
  if (!origin) {
    return {
      origin: null,
      nextNode: null,
      previousNode: null,
    };
  }
  const walker = document.createTreeWalker(
    document.body,
    NodeFilter.SHOW_ELEMENT,
    {
      acceptNode(node) {
        if ((node as Element).matches(focusableElementsSelector)) {
          return NodeFilter.FILTER_ACCEPT;
        }
        return NodeFilter.FILTER_SKIP;
      },
    },
  );
  walker.currentNode = origin;
  const previousNode = walker.previousNode() as FocusableElement;
  let nextNode = walker.nextNode() as FocusableElement;
  if (nextNode === origin) {
    nextNode = walker.nextNode() as FocusableElement;
  }
  return {
    origin: origin as FocusableElement,
    nextNode,
    previousNode,
  };
};

const getBorderRadius = ({
  isMobile,
  locale,
}: {
  isMobile: boolean;
  locale: Intl.Locale | undefined;
}): Style => {
  if (!isMobile) {
    return locale?.textInfo.direction === "ltr"
      ? {
          borderTopLeftRadius: "24px",
          borderBottomLeftRadius: "24px",
        }
      : {
          borderTopRightRadius: "24px",
          borderBottomRightRadius: "24px",
        };
  }
  return {};
};

/**
 * A dialog variant that does not block interaction with the main page content in desktop mode.
 *
 * Slideover does not have a backdrop, so the user can interact with the underlying content while the slideover is open.
 *
 * However, slideover closes when the content outside is interacted with, including pointer events and keyboard focus.
 *
 * To prevent the Slideover from closing when interacting with specific elements (e.g., tables or buttons that open the Slideover), wrap those elements in a {@link SlideoverPreventClose} component.
 */
export const Slideover = ({
  title,
  onClose,
  size = "s",
  render,
  ...slots
}: {
  /**
   * A callback function fired when a close action is triggered.
   *
   * If not provided, the close button will not be rendered.
   */
  onClose?: () => void;

  /**
   * Determines slideover width in desktop mode.
   * @defaultValue "s"
   */
  size?: "s" | "m" | "l" | "xl";
} & (
  | {
      /**
       * The visible header text for the panel. This prop is required.
       *
       * Also serves as an accessible name for the whole dialog.
       */
      title: string;

      /**
       * Raw content of slideover.
       *
       * This content is rendered directly without any scrollable container or footer support.
       * For scrollable content with footer support, use the `main` prop instead.
       *
       */
      content: ReactNode;

      render?: never;
    }
  | {
      /**
       * The visible header text for the panel. This prop is required.
       *
       * Also serves as an accessible name for the whole Slideover.
       */
      title: string;

      /**
       * The primary content of the slideover.
       *
       * This area will become scrollable if its content overflows the available space.
       */
      main: ReactNode;

      /**
       * A dedicated slot for footer content, such as action buttons.
       *
       * The footer is "sticky" and always remains visible at the bottom, it is not part of the scrollable area.
       */
      footer: ReactNode;

      render?: never;
    }
  | {
      title?: never;

      content?: never;
      /**
       * Function which returns content for the slideover.
       * Use this function, if you need to get a specific slideover content structure.
       * It is recommended to use the titleId to link to your title by id attribute,
       * in order to maintain accessibility
       *
       * @example
       * ```
       * <Slideover
       * render={(titleId) => <div id={titleId}>Title</div>}
       * />
       * ```
       */
      render: (titleId: string) => ReactNode;
    }
)) => {
  const locale = useLocale();
  const isMobile = useIsMobile();
  const typo = useTypo();
  const uiColors = useUiColors();
  const panelRef = useRef<HTMLDivElement>(null);
  const focusOriginRef = useRef<Node | null>(document.activeElement);
  const [nestedContainer, setNestedContainer] = useState<HTMLDivElement | null>(
    null,
  );
  const currentOnCloseRef = useRef<(() => void) | undefined>(onClose);
  const [popoverContainer, setPopoverContainer] =
    useState<HTMLDivElement | null>(null);
  const [nestedSlideoverStack, setNestedSlideoverStack] = useState<
    (() => void)[]
  >([]);
  const titleId = useId();
  const slideoverContext = useContext(SlideoverContext);
  const dialogContext = useContext(DialogContext);
  const snackbarContext = useSnackbarContext();
  if (!slideoverContext)
    throw new Error("Slideover must have an access to SlideoverContext");
  const {
    container,
    onStackChange,
    preventCloseElements,
    isNested,
    addPreventCloseElement,
    removePreventCloseElement,
  } = slideoverContext;

  useLayoutEffect(() => {
    currentOnCloseRef.current = onClose;
  }, [onClose]);

  const { overlayProps } = useOverlay(
    {
      onClose,
      isKeyboardDismissDisabled: !onClose,
      isDismissable: false,
      isOpen: true,
    },
    panelRef,
  );

  const maxWidth = (() => {
    if (isMobile) return "100%";
    switch (size) {
      case "s":
        return "360px";
      case "m":
        return "448px";
      case "l":
        return "528px";
      case "xl":
        return "764px";
      default:
        return "448px";
    }
  })();

  const maxHeight = "100%";
  const borderRadius = getBorderRadius({ isMobile, locale });

  const nestedContextValue = useMemo(
    () => ({
      container: nestedContainer,
      onStackChange: setNestedSlideoverStack,
      preventCloseElements,
      addPreventCloseElement,
      removePreventCloseElement,
      isNested: true,
    }),
    [
      nestedContainer,
      setNestedSlideoverStack,
      preventCloseElements,
      addPreventCloseElement,
      removePreventCloseElement,
    ],
  );

  useLayoutEffect(() => {
    const currentOnClose = currentOnCloseRef.current;
    if (!currentOnClose) return;

    onStackChange((prevStack) => {
      // Close all previous
      prevStack.forEach((callback) => {
        if (callback !== currentOnClose) {
          try {
            callback();
          } catch (e) {
            console.error(e);
          }
        }
      });

      // Only current remains
      return [currentOnClose];
    });

    return () => {
      onStackChange((prevStack) =>
        prevStack.filter((callback) => callback !== currentOnClose),
      );
    };
  }, [onStackChange]);

  useEffect(() => {
    if (!focusOriginRef.current) {
      focusOriginRef.current = document.activeElement as HTMLElement | null;
    }

    panelRef.current?.focus();

    return () => {
      const el = focusOriginRef.current;
      if (isElementFocusable(el)) {
        el.focus({ preventScroll: true });
      } else {
        const { nextNode, previousNode } = getNearestNodes(el);
        if (isElementFocusable(nextNode)) {
          nextNode.focus();
        } else if (isElementFocusable(previousNode)) {
          previousNode.focus();
        }
      }
      focusOriginRef.current = null;
    };
  }, []);

  useEffect(() => {
    if (!onClose || !preventCloseElements) return;

    const handleInteraction = (event: Event) => {
      const panel = panelRef.current;
      const target = event.target;

      if (!panel || !(target instanceof Node) || isNested) return;

      if (panel.contains(target)) return;

      if (nestedContainer?.contains(target)) return;

      if (popoverContainer?.contains(target)) return;

      if (dialogContext?.container?.contains(target)) return;

      if (snackbarContext?.container?.contains(target)) return;

      for (const root of preventCloseElements) {
        if (root.contains(target)) return;
      }

      // Close all nested
      nestedSlideoverStack.forEach((callback) => callback());

      // Close current
      onClose();
    };

    document.addEventListener("pointerdown", handleInteraction);
    document.addEventListener("focusin", handleInteraction);

    return () => {
      document.removeEventListener("pointerdown", handleInteraction);
      document.removeEventListener("focusin", handleInteraction);
    };
  }, [
    onClose,
    preventCloseElements,
    dialogContext,
    snackbarContext,
    nestedContainer,
    popoverContainer,
    nestedSlideoverStack,
    isNested,
  ]);

  if (!container) {
    return null;
  }

  return createPortal(
    <SlideoverContextProvider value={nestedContextValue}>
      <PopoverContextProvider value={{ container: popoverContainer }}>
        <div
          css={{
            color: uiColors.text,
            position: "fixed",
            top: "0",
            right: "0",
            bottom: "0",
            width: maxWidth,
            ...borderRadius,
            ...cssFns.boxShadow({
              offsetY: "8px",
              blurRadius: "20px",
              color: cssFns.setOpacity(uiColors.everBack, 0.12),
            }),
            pointerEvents: "auto",
          }}
        >
          <div
            css={{
              height: "100%",
              overflowY: "scroll",
              ...cssFns.overscrollBehavior("none"),
              scrollbarWidth: "none",
            }}
          >
            <div
              css={{
                minHeight: "110vh",
                display: "flex",
              }}
            >
              <section
                role="presentation"
                css={{
                  ...cssFns.placeContent("flex-end"),
                  position: "sticky",
                  top: "0",
                  display: "flex",
                  height: "100vh",
                  width: maxWidth,
                }}
              >
                <div
                  ref={panelRef}
                  tabIndex={0}
                  role="region"
                  aria-labelledby={titleId}
                  {...overlayProps}
                  css={{
                    outlineStyle: "none",
                    backgroundColor: uiColors.background,
                    maxWidth,
                    minWidth: maxWidth,
                    maxHeight,
                    display: "grid",
                    gridTemplateRows: title ? "max-content 1fr" : "1fr",
                    ...borderRadius,
                    ...cssFns.overflow("hidden"),
                    isolation: "isolate",
                  }}
                >
                  {render ? (
                    render(titleId)
                  ) : (
                    <>
                      <div
                        css={{
                          display: "grid",
                          alignItems: "flex-start",
                          minHeight: "60px",
                          boxSizing: "border-box",
                          paddingBlockStart: "8px",
                          paddingBlockEnd: "4px",
                          paddingInlineStart: "16px",
                          paddingInlineEnd: "8px",
                          gridTemplateColumns: "1fr auto",
                          columnGap: "8px",
                        }}
                      >
                        <div
                          id={titleId}
                          lang={locale.language}
                          css={{
                            display: "flex",
                            hyphens: "auto",
                            wordBreak: "break-word",
                            maxWidth: "100%",
                            paddingBlockStart: "10px",
                            ...typo({
                              level: "title4",
                              weight: "medium",
                              density: "normal",
                            }),
                            ...cssFns.margin("0px"),
                          }}
                        >
                          {title}
                        </div>
                        {onClose && (
                          <Button
                            variant="ghost"
                            icon={icons.CrossL}
                            onPress={onClose}
                            ariaLabel="Close"
                          />
                        )}
                      </div>
                      <div
                        css={{
                          display: "grid",
                          boxSizing: "border-box",
                          ...cssFns.overflow("hidden"),
                          ...(isMobile && {
                            ...cssFns.overflow("auto"),
                          }),
                        }}
                      >
                        {"main" in slots && "footer" in slots ? (
                          <SlideoverContent
                            main={slots.main}
                            footer={slots.footer}
                          />
                        ) : (
                          slots.content
                        )}
                      </div>
                    </>
                  )}
                </div>
              </section>
            </div>
            <div ref={(element) => setPopoverContainer(element)} />
            <div ref={(element) => setNestedContainer(element)} />
          </div>
        </div>
      </PopoverContextProvider>
      ,
    </SlideoverContextProvider>,
    container,
  );
};

const SlideoverContent = ({
  main,
  footer,
}: {
  main: ReactNode;
  footer?: ReactNode;
}) => {
  const uiColors = useUiColors();
  const parentRef = useRef<HTMLDivElement>(null);
  const innerRef = useRef<HTMLDivElement>(null);
  const customScrollbarCSS = useCustomScrollbar();
  const [isChildOverflow, setIsChildOverflow] = useState(false);

  useLayoutEffect(() => {
    if (!innerRef.current || !parentRef.current) return;
    const el = innerRef.current;
    const parent = parentRef.current;
    const observer = new ResizeObserver(() => {
      setIsChildOverflow(el.scrollHeight - 8 >= parent.clientHeight);
    });
    observer.observe(el);
    return () => {
      observer.unobserve(el);
    };
  }, [setIsChildOverflow]);

  return (
    <div
      css={{
        display: "grid",
        gridTemplateRows: "1fr max-content",
        ...cssFns.overflow("hidden"),
      }}
    >
      <div
        ref={parentRef}
        css={{
          overflowY: "scroll",
        }}
        {...customScrollbarCSS}
      >
        <div
          ref={innerRef}
          css={{
            display: "grid",
            ...cssFns.padding("16px"),
            paddingBlockStart: "0px",
            ...(isChildOverflow && { paddingBlockEnd: "8px" }),
          }}
        >
          {main}
        </div>
      </div>
      {footer ? (
        <div
          css={{
            ...(isChildOverflow && {
              borderTopLeftRadius: "24px",
              borderTopRightRadius: "24px",
            }),
            ...(isChildOverflow &&
              cssFns.boxShadow({
                offsetY: "-4px",
                blurRadius: "20px",
                color: cssFns.setOpacity(uiColors.everBack, 0.12),
              })),
            backgroundColor: isChildOverflow
              ? uiColors.backgroundFloating
              : uiColors.background,
          }}
        >
          {footer}
        </div>
      ) : undefined}
    </div>
  );
};
/**
 * The area that does not close the Slideover when interacted with.
 *
 * Use it to wrap elements that open the Slideover on click to prevent it from auto-closing.
 */
export const SlideoverPreventClose = ({
  children,
}: {
  children: ReactNode;
}) => {
  const slideoverContext = useContext(SlideoverContext);
  const elementRef = useRef<HTMLDivElement | null>(null);
  const addPreventCloseElement = slideoverContext?.addPreventCloseElement;
  const removePreventCloseElement = slideoverContext?.removePreventCloseElement;

  const setScopeRef = useCallback(
    (element: HTMLDivElement | null) => {
      if (element) {
        elementRef.current = element;
        addPreventCloseElement?.(element);
      } else {
        const current = elementRef.current;
        if (current) {
          removePreventCloseElement?.(current);
          elementRef.current = null;
        }
      }
    },
    [addPreventCloseElement, removePreventCloseElement],
  );

  return (
    <div ref={setScopeRef} css={{ display: "contents" }}>
      {children}
    </div>
  );
};

/**
 * Provider component that encapsulates state and auto-close logic.
 */
export const SlideoverProvider = ({
  children,
  container,
  onStackChange,
}: {
  children: ReactNode;
  container: HTMLDivElement | null;
  onStackChange: (value: SetStateAction<(() => void)[]>) => void;
}) => {
  const uiOptions = useUiOptions();

  const [preventCloseElements, setPreventCloseElements] = useState<
    Set<HTMLElement>
  >(() => new Set());

  const addPreventCloseElement = useCallback((el: HTMLElement) => {
    setPreventCloseElements((prev) => new Set(prev).add(el));
  }, []);

  const removePreventCloseElement = useCallback((el: HTMLElement | null) => {
    if (!el) return;

    setPreventCloseElements((prev) => {
      const next = new Set(prev);
      next.delete(el);
      return next;
    });
  }, []);

  const enableAutoClose = uiOptions.experimental?.slideoverAutoClose;

  const contextValue = useMemo(
    () => ({
      container,
      onStackChange,
      ...(enableAutoClose
        ? {
            preventCloseElements,
            addPreventCloseElement,
            removePreventCloseElement,
          }
        : undefined),
    }),
    [
      container,
      onStackChange,
      enableAutoClose,
      preventCloseElements,
      addPreventCloseElement,
      removePreventCloseElement,
    ],
  );

  return (
    <SlideoverContextProvider value={contextValue}>
      {children}
    </SlideoverContextProvider>
  );
};
